import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { useLoaderData, Link } from "@remix-run/react";
import { json } from "@remix-run/cloudflare";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Input } from "~/components/ui/input";
import { ModGrid } from "~/components/mods/ModGrid";
import { ModCard } from "~/components/mods/ModCard";
import { 
  MessageSquare, 
  Search, 
  Share2, 
  Users, 
  TrendingUp,
  Clock,
  Pin,
  ArrowRight,
  Plus,
  Eye,
  Heart,
  Star,
  Megaphone,
  HelpCircle,
  Upload,
  Filter,
  Bell,
  UserCheck,
  Flame,
  Gift,
  Shield,
  Target,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useState } from "react";
import { createSupabaseAdminClient } from "~/lib/supabase.server";
import { getBatchModStats } from "~/lib/mods.server";
import type { Database } from "~/types/database";

// 定义从数据库查询返回的mod类型
type ModFromDB = Database['public']['Tables']['mods']['Row'] & {
  author: Database['public']['Tables']['users']['Row']
  category: Database['public']['Tables']['categories']['Row'] | null
  mod_images?: Database['public']['Tables']['mod_images']['Row'][]
  mod_tags?: (Database['public']['Tables']['mod_tags']['Row'] & {
    tag: Database['public']['Tables']['tags']['Row']
  })[]
}

export const meta: MetaFunction = () => {
  return [
    { title: "Mods Community Forum | stellarblademod.com" },
    { 
      name: "description", 
      content: "Join the Stellar Blade modding community. Share mods, request help, discuss techniques, and connect with fellow modders." 
    },
    { name: "keywords", content: "stellar blade mods, modding community, mod sharing, mod requests, stellar blade forum" },
    { name: "robots", content: "index, follow" },
    
    // Canonical URL  
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/mods" },
    
    // Open Graph
    { property: "og:title", content: "Stellar Blade Mods Community Forum" },
    { property: "og:description", content: "Join the Stellar Blade modding community. Share mods, request help, and connect with fellow modders." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/mods" },
  ];
};

// Loader函数获取真实的mods数据
export async function loader({ context }: LoaderFunctionArgs) {
  try {
    // 处理本地开发环境和生产环境的差异
    const isProduction = process.env.NODE_ENV === 'production';
    const env = isProduction ? (context as any)?.cloudflare?.env : {
      SUPABASE_URL: process.env.SUPABASE_URL,
      SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
      SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
    };
    
    const supabase = createSupabaseAdminClient(env);

    // 获取已批准的mods及其相关数据
    const { data: mods, error: modsError } = await supabase
      .from('mods')
      .select(`
        *,
        author:users!mods_author_id_fkey(*),
        category:categories(*),
        mod_images(*),
        mod_tags(
          tag:tags(*)
        )
      `)
      .eq('status', 'APPROVED')
      .order('created_at', { ascending: false })
      .limit(20);

    if (modsError) {
      console.error('Error fetching mods:', modsError);
    }

    // 使用批量统计查询优化性能
    const modIds = (mods || []).map(mod => mod.id);
    const batchStats = await getBatchModStats(supabase, modIds);
    
    // 将统计数据合并到mod对象中
    const modsWithStats = (mods || []).map((mod) => {
      const stats = batchStats[mod.id] || { download_count: 0, review_count: 0, rating: 0 };
      
      // 调试：输出图片信息
      console.log(`Mod ${mod.title} images:`, {
        modId: mod.id,
        imageCount: mod.mod_images?.length || 0,
        images: mod.mod_images?.map((img: any) => ({
          id: img.id,
          image_type: img.image_type,
          is_primary: img.is_primary,
          image_url: img.image_url,
          url: img.url,
          sort_order: img.sort_order,
          fullObject: img
        }))
      });
      
      return {
        ...mod,
        download_count: stats.download_count,
        review_count: stats.review_count,
        rating: stats.rating,
        mod_images: (mod.mod_images || []).sort((a: any, b: any) => a.sort_order - b.sort_order)
      };
    });

    console.log(`Found ${modsWithStats.length} mods with images processed`);

    return json({
      mods: modsWithStats || [],
      forumCategories,
      featuredPosts,
      trendingTopics,
      onlineUsers
    });

  } catch (error) {
    console.error('Loader error:', error);
    return json({
      mods: [],
      forumCategories,
      featuredPosts,
      trendingTopics,
      onlineUsers
    });
  }
}

// Sample forum data - will be replaced with real data from database
const forumCategories = [
  {
    id: "announcements",
    title: "Announcement Zone",
    description: "Official updates and important news",
    icon: "Megaphone",
    color: "from-orange-500 to-red-500",
    postCount: 23,
    todayPosts: 2,
    topics: [
      { name: "Site Updates", count: 8 },
      { name: "Mod Updates", count: 12 },
      { name: "Community Events", count: 3 }
    ],
    latestPost: {
      title: "New mod upload system now available",
      author: "Administrator",
      time: "1 day ago",
      replies: 67,
      isOfficial: true
    }
  },
  {
    id: "sharing",
    title: "Share Zone", 
    description: "Share your mods and creations",
    icon: "Share2",
    color: "from-green-500 to-emerald-500",
    postCount: 234,
    todayPosts: 8,
    topics: [
      { name: "Mod Releases", count: 98 },
      { name: "Screenshots", count: 76 },
      { name: "Video Showcases", count: 60 }
    ],
    latestPost: {
      title: "New EVE outfit collection released!",
      author: "ModArtist",
      time: "1 hour ago", 
      replies: 28,
      isHot: true
    }
  },
  {
    id: "discussion",
    title: "Discussion Zone",
    description: "General mod discussions and reviews",
    icon: "MessageSquare",
    color: "from-purple-500 to-pink-500", 
    postCount: 312,
    todayPosts: 15,
    topics: [
      { name: "General Discussion", count: 145 },
      { name: "Mod Reviews", count: 89 },
      { name: "Bug Reports", count: 78 }
    ],
    latestPost: {
      title: "Best graphics enhancement mods comparison",
      author: "TechReviewer",
      time: "30 minutes ago",
      replies: 45,
      isPinned: false
    }
  },
  {
    id: "requests",
    title: "Request Zone",
    description: "Looking for specific mods or need help?",
    icon: "HelpCircle",
    color: "from-blue-500 to-cyan-500",
    postCount: 156,
    todayPosts: 12,
    topics: [
      { name: "Mod Requests", count: 89 },
      { name: "Technical Help", count: 42 },
      { name: "Feature Suggestions", count: 25 }
    ],
    latestPost: {
      title: "Looking for a combat difficulty mod",
      author: "StellarFan2024",
      time: "2 hours ago",
      replies: 12,
      isNew: true
    }
  } 
];

const featuredPosts = [
  {
    id: "1",
    title: "🔥 Complete Modding Guide for Beginners",
    author: "ModMaster",
    category: "Guide",
    replies: 156,
    views: 2340,
    isPinned: true,
    time: "3 days ago",
    excerpt: "Everything you need to know to start modding Stellar Blade safely and effectively...",
    isHot: true
  },
  {
    id: "2", 
    title: "🎨 Monthly Mod Contest - Theme: Combat Enhancement",
    author: "CommunityManager",
    category: "Contest",
    replies: 89,
    views: 1890,
    isPinned: true,
    time: "1 week ago",
    excerpt: "Submit your best combat-related mods for a chance to win prizes...",
    hasReward: true
  },
  {
    id: "3",
    title: "Best Performance Optimization Mods - 2025 Edition",
    author: "PerformanceGuru",
    category: "Review",
    replies: 234,
    views: 5670,
    isPinned: false,
    time: "2 days ago",
    excerpt: "Comprehensive review of the most effective performance mods currently available...",
    isExpert: true
  }
];

const trendingTopics = [
  { name: "UE4SS Setup", posts: 45, trend: "+12" },
  { name: "Character Customization", posts: 38, trend: "+8" },
  { name: "Combat Mods", posts: 32, trend: "+15" },
  { name: "Graphics Enhancement", posts: 29, trend: "+6" },
  { name: "Bug Fixes", posts: 24, trend: "+3" }
];

const onlineUsers = [
  { name: "ModMaster", role: "Expert", isOnline: true },
  { name: "StellarFan2024", role: "Member", isOnline: true },
  { name: "TechReviewer", role: "Moderator", isOnline: true },
  { name: "ArtistPro", role: "Creator", isOnline: false },
  { name: "Administrator", role: "Admin", isOnline: true }
];

export default function ModsIndex() {
  const { mods, forumCategories, featuredPosts, trendingTopics, onlineUsers } = useLoaderData<typeof loader>();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [scrollPosition, setScrollPosition] = useState(0);

  // Editor's Choice carousel logic - Fixed mods
  const cardWidth = 320; // w-80 = 20rem = 320px
  const gap = 24; // gap-6 = 1.5rem = 24px
  const itemWidth = cardWidth + gap;
  
  // Fixed featured mods data - 写死的精选MOD
  const featuredMods = [
    {
      id: "eve2scarlet",
      title: "EVE2Scarlet",
      slug: "eve2scarlet",
      description: "Transform EVE into Scarlet with complete appearance overhaul including face, hair, body and weapon modifications.",
      version: "1.0.0",
      author_id: "likefengzi-id",
      category_id: "outfits-id",
      mod_type: "CHARACTER",
      download_count: 4523,
      rating: 0, // 让ModCard组件生成随机评分
      review_count: 296,
      author: { 
        id: "likefengzi-id", 
        username: "likefengzi",
        display_name: "likefengzi"
      },
      category: { 
        id: "outfits-id", 
        name: "Outfits", 
        slug: "outfits"
      },
      mod_images: [{ 
        id: "img-eve2scarlet", 
        mod_id: "eve2scarlet", 
        url: "/mods/eve2scarlet1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "improved-perfect-defense",
      title: "Improved Perfect Defense",
      slug: "improved-perfect-defense",
      description: "Slightly increased perfect parry/dodge windows and decreased guard cooldown for better responsive gameplay.",
      version: "1.01",
      author_id: "andrew-id",
      category_id: "gameplay-id",
      mod_type: "GAMEPLAY",
      download_count: 8247,
      rating: 0, // 让ModCard组件生成随机评分
      review_count: 540,
      author: { 
        id: "andrew-id", 
        username: "andrewRCr",
        display_name: "Andrew Creekmore"
      },
      category: { 
        id: "gameplay-id", 
        name: "Gameplay", 
        slug: "gameplay"
      },
      mod_images: [{ 
        id: "img-ipd", 
        mod_id: "improved-perfect-defense", 
        url: "/mods/ImprovedPerfectDefense1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "minervamon",
      title: "Minervamon",
      slug: "minervamon",
      description: "Transform the Fluffy Bear outfit into Minervamon from Digimon with complete visual overhaul.",
      version: "1.0",
      author_id: "hallow-id",
      category_id: "outfits-id",
      mod_type: "CHARACTER",
      download_count: 1247,
      rating: 0, // 让ModCard组件生成随机评分
      review_count: 31,
      author: { 
        id: "hallow-id", 
        username: "HallowSL",
        display_name: "Hallow"
      },
      category: { 
        id: "outfits-id", 
        name: "Outfits", 
        slug: "outfits"
      },
      mod_images: [{ 
        id: "img-minervamon", 
        mod_id: "minervamon", 
        url: "/mods/minervamon1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "ultimate-engine-tweaks",
      title: "Ultimate Engine Tweaks",
      slug: "ultimate-engine-tweaks",
      description: "Definitive Unreal Engine.ini optimizations to remove stutters, improve performance and enhance clarity.",
      version: "1.0",
      author_id: "p40l0-id",
      category_id: "performance-id",
      mod_type: "OPTIMIZATION",
      download_count: 12840,
      rating: 0, // 让ModCard组件生成随机评分
      review_count: 763,
      author: { 
        id: "p40l0-id", 
        username: "P40L0",
        display_name: "P40L0"
      },
      category: { 
        id: "performance-id", 
        name: "Performance", 
        slug: "performance"
      },
      mod_images: [{ 
        id: "img-ue-tweaks", 
        mod_id: "ultimate-engine-tweaks", 
        url: "/mods/UltimateEngineTweaks1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "mod-hub",
      title: "Mod Hub - Stellar Blade Mod Manager",
      slug: "mod-hub",
      description: "The ultimate Nexus Mods manager for Stellar Blade. Seamlessly download, install, and manage your mod collection.",
      version: "0.10.10",
      author_id: "dekita-id",
      category_id: "utility-id",
      mod_type: "UTILITY",
      download_count: 2347,
      rating: 0, // 让ModCard组件生成随机评分
      review_count: 156,
      author: { 
        id: "dekita-id", 
        username: "Dekita",
        display_name: "DekitaRPG"
      },
      category: { 
        id: "utility-id", 
        name: "Utility", 
        slug: "utility"
      },
      mod_images: [{ 
        id: "img-mod-hub", 
        mod_id: "mod-hub", 
        url: "/mods/modhub1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    }
  ] as any[];
  
  const totalMods = featuredMods.length;
  const maxScroll = Math.max(0, (totalMods - 1) * itemWidth);
  
  const canGoPrev = scrollPosition > 0;
  const canGoNext = scrollPosition < maxScroll;
  
  const handlePrev = () => {
    if (canGoPrev) {
      setScrollPosition(Math.max(0, scrollPosition - itemWidth));
    }
  };
  
  const handleNext = () => {
    if (canGoNext) {
      setScrollPosition(Math.min(maxScroll, scrollPosition + itemWidth));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Hero Section */}
      <div className="relative text-white py-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/mods/Stellar-Blade-Nikke.jpg')"
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-purple-900/60 to-black/70 backdrop-blur-[2px]" />
        
        {/* Content */}
        <div className="container relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Stellar Blade Mods Community
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Connect with fellow modders, share your creations, find the mods you need, and help shape the future of Stellar Blade modding
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input 
                  placeholder="Search posts, mods, or users..."
                  className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-10 px-8">
                <Plus className="mr-2 h-5 w-5" />
                Create New Post
              </Button>
              <Button variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:border-white/60 h-10 px-8 backdrop-blur-sm" asChild>
                <Link to="/mods/upload">
                  <Upload className="mr-2 h-5 w-5" />
                  Upload Mod
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">

            {/* Editor's Choice Section */}
            <section id="editors-choice">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold flex items-center gap-2">
                    <Star className="h-8 w-8 text-yellow-500" />
                    Editor's Choice
                  </h2>
                  <p className="text-muted-foreground mt-2">
                    Carefully selected mods by our editors
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrev}
                    disabled={!canGoPrev}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNext}
                    disabled={!canGoNext}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Horizontal scrolling mod cards */}
              <div className="overflow-hidden">
                <div 
                  className="flex gap-6 transition-transform duration-300 ease-in-out"
                  style={{ 
                    transform: `translateX(-${scrollPosition}px)`,
                    width: 'max-content'
                  }}
                >
                  {featuredMods.map((mod) => (
                    <div key={mod.id} className="flex-shrink-0 w-80">
                      <ModCard mod={mod} />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Available Mods Section */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-purple-500" />
                  <h2 className="text-2xl font-bold">Available Mods</h2>
                </div>
              </div>

              {/* Mods Grid */}
              <ModGrid mods={mods} className="mb-8" />
            </section>

            {/* Featured Posts */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Pin className="h-5 w-5 text-orange-500" />
                  <h2 className="text-2xl font-bold">Featured Posts (Coming Soon)</h2>
                </div>
              </div>

              <div className="space-y-4">
                {featuredPosts.map((post) => (
                  <Card key={post.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {post.isPinned && (
                              <Pin className="h-4 w-4 text-orange-500" />
                            )}
                            {(post as any).isHot && (
                              <Flame className="h-4 w-4 text-red-500" />
                            )}
                            {(post as any).hasReward && (
                              <Gift className="h-4 w-4 text-yellow-500" />
                            )}
                            {(post as any).isExpert && (
                              <Shield className="h-4 w-4 text-blue-500" />
                            )}
                            <Badge variant="secondary">{post.category}</Badge>
                            <span className="text-sm text-muted-foreground">{post.time}</span>
                          </div>
                          <h3 className="text-lg font-semibold mb-2 hover:text-purple-600 transition-colors">
                            <Link to={`/mods/posts/${post.id}`}>
                              {post.title}
                            </Link>
                          </h3>
                          <p className="text-muted-foreground mb-3">{post.excerpt}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>by {post.author}</span>
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-4 w-4" />
                              {post.replies}
                            </div>
                            <div className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              {post.views}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Forum Categories */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Forum Categories (Coming Soon)</h2>
                <div className="flex items-center gap-2">
                  <select 
                    className="px-3 py-1 text-sm border rounded-md"
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    <option value="active">Most Active</option>
                    <option value="new">Newest Posts</option>
                  </select>
                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    Filter
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {forumCategories.map((category) => {
                  // 根据字符串名称获取图标组件
                  const getIconComponent = (iconName: string) => {
                    switch (iconName) {
                      case 'HelpCircle': return HelpCircle;
                      case 'Share2': return Share2;
                      case 'MessageSquare': return MessageSquare;
                      case 'Megaphone': return Megaphone;
                      default: return MessageSquare;
                    }
                  };
                  const IconComponent = getIconComponent(category.icon);
                  return (
                    <Card key={category.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-lg bg-gradient-to-r ${category.color}`}>
                              <IconComponent className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <CardTitle>
                                {category.title}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground">
                                {category.description}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline">{category.postCount}</Badge>
                            <p className="text-xs text-green-600 mt-1">+{category.todayPosts} today</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {category.topics.map((topic) => (
                              <Badge key={topic.name} variant="secondary" className="text-xs">
                                {topic.name} ({topic.count})
                              </Badge>
                            ))}
                          </div>
                          <Separator />
                          <div className="text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              {(category.latestPost as any).isNew && (
                                <Badge variant="destructive" className="text-xs px-1 py-0">NEW</Badge>
                              )}
                              {(category.latestPost as any).isHot && (
                                <Badge variant="secondary" className="text-xs px-1 py-0 bg-red-100 text-red-700">HOT</Badge>
                              )}
                              {(category.latestPost as any).isOfficial && (
                                <Badge variant="secondary" className="text-xs px-1 py-0 bg-blue-100 text-blue-700">OFFICIAL</Badge>
                              )}
                              <p className="font-medium">Latest: {category.latestPost.title}</p>
                            </div>
                            <div className="flex items-center justify-between text-muted-foreground">
                              <span>by {category.latestPost.author}</span>
                              <span>{category.latestPost.time}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 h-[calc(100vh-8rem)] overflow-y-auto" style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent'
            }}>
              <div className="space-y-6 pr-2">
                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button className="w-full" variant="outline" asChild>
                      <Link to="/mods/upload">
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Mod
                      </Link>
                    </Button>
                    <Button className="w-full" variant="outline" asChild>
                      <Link to="/contact">
                        <HelpCircle className="mr-2 h-4 w-4" />
                        Request Help
                      </Link>
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Users className="mr-2 h-4 w-4" />
                      Request Mod (Coming Soon)
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Bell className="mr-2 h-4 w-4" />
                      Notifications (Coming Soon)
                    </Button>
                  </CardContent>
                </Card>

                {/* Online Users */}
                <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Who's Online
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {onlineUsers.map((user) => (
                    <div key={user.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${user.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                        <span className="text-sm">{user.name}</span>
                        <Badge variant="outline" className="text-xs">{user.role}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Community Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Community Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Total Posts</span>
                  <span className="font-bold">2,847</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Users</span>
                  <span className="font-bold">1,234</span>
                </div>
                <div className="flex justify-between">
                  <span>Mods Shared</span>
                  <span className="font-bold">{mods.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Solved Requests</span>
                  <span className="font-bold text-green-600">89%</span>
                </div>
              </CardContent>
            </Card>

            {/* Trending Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Trending Topics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trendingTopics.map((topic, index) => (
                    <div key={topic.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-muted-foreground">
                          #{index + 1}
                        </span>
                        <span className="text-sm text-muted-foreground cursor-not-allowed">
                          {topic.name}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-muted-foreground">
                          {topic.posts} posts
                        </span>
                        <p className="text-xs text-green-600">{topic.trend}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>



            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p><strong>ModMaster</strong> posted in Combat Mods</p>
                      <p className="text-muted-foreground">2 minutes ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p><strong>StellarFan</strong> requested help</p>
                      <p className="text-muted-foreground">5 minutes ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                    <div>
                      <p><strong>ArtistPro</strong> shared screenshots</p>
                      <p className="text-muted-foreground">10 minutes ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                    <div>
                      <p><strong>Administrator</strong> made announcement</p>
                      <p className="text-muted-foreground">1 hour ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 